import { useSession } from 'next-auth/react';

import { useFeatureFlag } from './useFeatureFlag';

// Which dashboard a viewer gets. Two flags open the insights surface and the
// one a viewer holds decides how much of it they see:
//
//   • admin   (ADMIN_SCORE_INSIGHTS)   — the district/school-wide dashboard:
//     every school, every teacher, every student response.
//   • teacher (insights_teacher_dashboard) — their own classes and lessons, plus
//     school and by-grade aggregates. No colleague is named.
//
// Admin wins when a viewer holds both, so switching the teacher flag on for a
// whole district never downgrades its admins.
export type InsightsRole = 'admin' | 'teacher';

type AccessInput = {
  adminFlag: boolean;
  teacherFlag: boolean;
  // The Gaps (TEKS standards) lens is dark-launched behind its own flag per
  // role, layered on top of the enrolment flag so it can roll out to admins and
  // to teachers independently.
  gapsAdminFlag: boolean;
  gapsTeacherFlag: boolean;
  // The Skill gaps lens rides on top of the viewer's gaps flag.
  skillGapsFlag: boolean;
  isAuthenticated: boolean;
  viewerId: string | null;
};

export type InsightsAccess = {
  role: InsightsRole | null;
  isTeacherView: boolean;
  // The viewer's own user id — the teacher dashboard is scoped to it.
  viewerId: string | null;
  flagOn: boolean;
  gapsLensOn: boolean;
  // The Skill gaps lens and its pages: the gaps lens plus skill_gaps_v2.
  skillGapsOn: boolean;
  isAuthenticated: boolean;
  shouldBlock: boolean;
};

// Pure so the role matrix (admin wins, gaps per role, 404 for neither) is
// testable without a session or an Unleash provider.
export function resolveInsightsAccess({
  adminFlag,
  teacherFlag,
  gapsAdminFlag,
  gapsTeacherFlag,
  skillGapsFlag,
  isAuthenticated,
  viewerId,
}: AccessInput): InsightsAccess {
  const role: InsightsRole | null = adminFlag ? 'admin' : teacherFlag ? 'teacher' : null;
  const gapsLensOn =
    role === 'admin' ? gapsAdminFlag : role === 'teacher' && gapsTeacherFlag;
  return {
    role,
    isTeacherView: role === 'teacher',
    viewerId,
    flagOn: role !== null,
    // Either gaps flag only governs whether the Gaps lens is live — the surface
    // itself still requires an enrolment flag.
    gapsLensOn,
    skillGapsOn: gapsLensOn && skillGapsFlag,
    isAuthenticated,
    shouldBlock: isAuthenticated && role === null,
  };
}

// Auth-aware feature-flag gate for the insights surface.
//
// Authenticated viewers without either flag 404 from the first client render —
// `getInsightsPageProps` provisions Unleash with the viewer's full context
// (including the impersonated email when present), so the bootstrap toggle
// list is already correct for them and the flags are trustworthy as soon as
// the page hydrates. Waiting for `flagsReady` here would briefly render
// the InsightsShell chrome to viewers who shouldn't see it at all before
// the 404 lands. Unauthenticated viewers fall through entirely so
// InsightsAuthGate can render the blurred preview + login card.
export function useInsightsAccess(): InsightsAccess {
  const session = useSession();
  return resolveInsightsAccess({
    adminFlag: useFeatureFlag('ADMIN_SCORE_INSIGHTS'),
    teacherFlag: useFeatureFlag('insights_teacher_dashboard'),
    gapsAdminFlag: useFeatureFlag('gaps-admin-dashboard'),
    gapsTeacherFlag: useFeatureFlag('insights_teacher_gaps'),
    skillGapsFlag: useFeatureFlag('skill_gaps_v2'),
    isAuthenticated: session.status === 'authenticated',
    viewerId: session.data?.user?.uid ?? null,
  });
}
